'use client';

import { AnimatePresence, motion } from 'framer-motion';
import LoginForm from '@/app/(pages)/login/Login/LoginForm';
import { Container } from '@/ui/Container';
import Link from 'next/link';
import React from 'react';
import { useSearchParams } from 'next/navigation';
import VKAuthRedirectButton from '@/app/(pages)/login/Login/components/VkAuthRedirectButton';
import YandexAuthRedirectButton from '@/app/(pages)/login/Login/components/YandexAuthRedirectButton';
import styled from 'styled-components';
import logotype from '../../../logotype.png';
import Image from 'next/image';
import { ROOT_WWW_URL } from '@/constants/url';

const FormWrapper = styled.div`
  width: 360px;
  padding: 40px;
  display: flex;
  flex-direction: column;
  gap: 40px;
  margin: 24px;

  @media (max-width: 480px) {
    padding: 32px 24px;
    margin: 20px 16px;
    border-radius: 16px;
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.06),
    0 1px 4px rgba(0, 0, 0, 0.03);
  }
`;

const Title = styled.h1`
  text-align: left;
  font-size: 32px;
  font-weight: 700;
  color: #111827;
  margin: 0;
  line-height: 1.2;
`;

const Subtitle = styled.p`
  text-align: left;
  font-size: 15px;
  color: #6b7280;
  margin-top: 12px;
  line-height: 1.5;
  font-weight: 400;
  max-width: 380px;

  @media (max-width: 480px) {
    font-size: 14px;
    margin-top: 10px;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 4px 0;

  img {
    height: 28px;
    width: auto;
  }
`;

const SocialButtonsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 8px;
`;

const LinksContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  margin-top: 24px;
  padding-top: 32px;
  position: relative;
  border-top: 1px solid #f3f4f6;

  @media (max-width: 480px) {
    padding-top: 28px;
    gap: 18px;
  }
`;

const TextLink = styled(Link)`
  color: #6b7280;
  font-size: 15px;
  font-weight: 400;
  text-decoration: none;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 6px 12px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;

  &:hover {
    color: #4b5563;
    background-color: #f9fafb;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
    background-color: #f3f4f6;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    padding: 5px 10px;
  }
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

export default function Login() {
  const searchParams = useSearchParams();
  const isDevelopment = process.env.NODE_ENV === 'development';

  return (
    <Container $isFullScreen={true}>
      <AnimatePresence mode="wait">
        <motion.div
          initial={{ opacity: 0, scale: 0.98, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{
            duration: 0.45,
            ease: [0.34, 1.56, 0.64, 1],
          }}
          exit={{ opacity: 0, scale: 0.98, y: -12 }}
        >
          <FormWrapper>
            <HeaderContainer>
              <LogoContainer>
                <Link
                  href={ROOT_WWW_URL}
                  target="_self"
                >
                  <Image
                    src={logotype}
                    alt=""
                    priority
                    style={{
                      objectFit: 'contain',
                    }}
                  />
                </Link>
              </LogoContainer>

              <div>
                <Title>Войти в аккаунт</Title>
                <Subtitle>
                  {isDevelopment
                    ? 'В режиме разработки вход через email'
                    : 'Если у вас еще нет аккаунта, он будет создан автоматически при первом входе через предложенные способы'
                  }
                </Subtitle>
              </div>
            </HeaderContainer>

            <ContentSection>
              {isDevelopment ? (
                <LoginForm />
              ) : (
                <SocialButtonsContainer>
                  <YandexAuthRedirectButton />
                  <VKAuthRedirectButton />
                </SocialButtonsContainer>
              )}
            </ContentSection>

            <LinksContainer>
              {isDevelopment ? (
                <TextLink
                  href={`/register?${searchParams.toString()}`}
                  target="_self"
                >
                  Создать аккаунт
                </TextLink>
              ) : (
                <TextLink
                  href={ROOT_WWW_URL}
                  target="_self"
                >
                  Вернуться на главную
                </TextLink>
              )}
            </LinksContainer>
          </FormWrapper>
        </motion.div>
      </AnimatePresence>
    </Container>
  );
}
